import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { injectIntl } from "react-intl";
import {
  Alert,
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  UncontrolledDropdown
} from "reactstrap";
import ReactTable from "react-table";
import "react-table/react-table.css";
import IntlMessages from "../../../helpers/IntlMessages";
import ShopeeWorkspaceContext from "./ShopeeWorkspaceContext";
import ShopeeV2Api from "../../../services/shopeeV2";
import { mergeShopeeCatalogFilters } from "./shopeeCatalogFilters";
import { mappingHasSyncError } from "./shopeeMappingStatus";
import { SHOPEE_INVENTORY_STATUS_LABEL_IDS } from "./ShopeeInventoryPanel";
import "./ShopeeInventory.scss";

const hasValue = value => value !== null && value !== undefined && value !== "";
const numberValue = value => {
  if (!hasValue(value)) return null;
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
};
const textValue = value => {
  if (!hasValue(value)) return null;
  const normalized = String(value).trim();
  return normalized || null;
};
const formatNumber = value => {
  const normalized = numberValue(value);
  return normalized === null ? "—" : normalized.toLocaleString("vi-VN");
};
const formatPercent = value => {
  const normalized = numberValue(value);
  return normalized === null
    ? "—"
    : `${normalized.toLocaleString("vi-VN", { maximumFractionDigits: 1 })}%`;
};
const formatTimestamp = value => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(date);
};
const percentOf = (value, total) => {
  const normalizedValue = numberValue(value) || 0;
  const normalizedTotal = numberValue(total) || 0;
  return normalizedTotal > 0 ? (normalizedValue / normalizedTotal) * 100 : 0;
};

export const money = value => {
  const normalized = numberValue(value);
  return normalized === null ? "—" : `${normalized.toLocaleString("vi-VN")} đ`;
};

const firstPriceInfo = value => {
  if (Array.isArray(value)) return value.find(Boolean) || null;
  return value && typeof value === "object" ? value : null;
};

export const getShopeeProviderPrice = mapping => {
  const source = mapping || {};
  const priceInfo = firstPriceInfo(source.platform_price_info || source.price_info);
  const candidates = [
    source.platform_price,
    source.shopee_price,
    source.platform_current_price,
    source.platform_original_price,
    source.current_price,
    source.original_price,
    priceInfo && priceInfo.current_price,
    priceInfo && priceInfo.original_price
  ];
  for (let index = 0; index < candidates.length; index += 1) {
    const price = numberValue(candidates[index]);
    if (price !== null) return price;
  }
  return null;
};

// Dummy image fallback SVG
const DummyImage = () => (
  <div className="img-placeholder">
    <i className="simple-icon-tag" />
  </div>
);

export const normalizeShopeePricingMapping = mapping => {
  const source = mapping || {};
  const vmassPrice = numberValue(source.vmass_price);
  const shopeePrice = getShopeeProviderPrice(source);
  const priceDiff = vmassPrice === null || shopeePrice === null ? null : shopeePrice - vmassPrice;
  const priceDiffPct = priceDiff === null || vmassPrice === 0 ? null : (priceDiff / vmassPrice) * 100;
  let pricingStatus = "unavailable";
  let statusLabelId = SHOPEE_INVENTORY_STATUS_LABEL_IDS.unknown;

  if (!source.product_id) {
    pricingStatus = "unmapped";
    statusLabelId = SHOPEE_INVENTORY_STATUS_LABEL_IDS.unmapped;
  } else if (mappingHasSyncError(source)) {
    pricingStatus = "error";
    statusLabelId = SHOPEE_INVENTORY_STATUS_LABEL_IDS.error;
  } else if (priceDiff === 0) {
    pricingStatus = "synced";
    statusLabelId = SHOPEE_INVENTORY_STATUS_LABEL_IDS.synced;
  } else if (priceDiff !== null) {
    pricingStatus = "variance";
    statusLabelId = SHOPEE_INVENTORY_STATUS_LABEL_IDS.variance;
  }

  return {
    ...source,
    id: source.id,
    name: textValue(source.vmass_product_name) || textValue(source.platform_name) || null,
    sku: textValue(source.vmass_sku) || textValue(source.platform_sku),
    barcode: textValue(source.vmass_barcode),
    image: textValue(source.vmass_image) || textValue(source.platform_image),
    vmass_price: vmassPrice,
    shopee_price: shopeePrice,
    price_diff: priceDiff,
    diff_amount: priceDiff === null ? null : Math.abs(priceDiff),
    diff_pct: priceDiffPct === null ? null : Math.abs(priceDiffPct),
    shopee_diff_pct: priceDiffPct,
    pricing_status: pricingStatus,
    status: pricingStatus === "synced" ? "dongbo" : "chenhlech",
    status_label_id: statusLabelId,
    updated_at: source.last_synced_at || source.updatedAt || null
  };
};

export const getShopeePricingMetrics = (mappings, meta = {}) => {
  const rows = (Array.isArray(mappings) ? mappings : []).map(normalizeShopeePricingMapping);
  const count = status => rows.filter(row => row.pricing_status === status).length;
  const timestamps = rows.map(row => new Date(row.updated_at).getTime()).filter(Number.isFinite);
  const metaTotal = numberValue(meta.total);
  return {
    total: metaTotal === null ? rows.length : metaTotal,
    loadedRows: rows.length,
    synced: count("synced"),
    variance: count("variance"),
    unavailable: count("unavailable") + count("unmapped"),
    unmapped: count("unmapped"),
    errors: count("error"),
    lastUpdatedAt: timestamps.length ? Math.max(...timestamps) : null
  };
};

export const getTopShopeePriceVariance = rows =>
  (Array.isArray(rows) ? rows : [])
    .filter(row => numberValue(row.price_diff) !== null && numberValue(row.price_diff) !== 0)
    .sort((left, right) => Math.abs(right.price_diff) - Math.abs(left.price_diff))
    .slice(0, 5);

export const getShopeePriceAlerts = rows => {
  const data = Array.isArray(rows) ? rows : [];
  return {
    higher: data.filter(row => numberValue(row.shopee_diff_pct) !== null && row.shopee_diff_pct > 10).length,
    lower: data.filter(row => numberValue(row.shopee_diff_pct) !== null && row.shopee_diff_pct < -10).length,
    unavailable: data.filter(row => ["unavailable", "unmapped", "error"].includes(row.pricing_status)).length
  };
};

export default injectIntl(function ShopeePricingPanel({ intl }) {
  const workspace = useContext(ShopeeWorkspaceContext) || {};
  const rawMappings = workspace.data && workspace.data.mappings;

  // Filter & tab states
  const [selectedChannel, setSelectedChannel] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRows, setSelectedRows] = useState([]);

  // Modals
  const [ruleModal, setRuleModal] = useState(false);
  const [approvalModal, setApprovalModal] = useState(false);

  // Price policy rule state
  const [policyMode, setPolicyMode] = useState("DIRECT");
  const [markupPercent, setMarkupPercent] = useState("");
  const [markupAmount, setMarkupAmount] = useState("");
  const [channelFeePercent, setChannelFeePercent] = useState("");
  const [packagingCost, setPackagingCost] = useState("");
  const [roundingRule, setRoundingRule] = useState("NONE");

  const mappings = useMemo(
    () => (Array.isArray(rawMappings) ? rawMappings : []).map(normalizeShopeePricingMapping),
    [rawMappings]
  );
  const metrics = useMemo(
    () => getShopeePricingMetrics(rawMappings, workspace.meta || {}),
    [rawMappings, workspace.meta]
  );
  const topPriceVarianceProducts = useMemo(
    () => getTopShopeePriceVariance(mappings),
    [mappings]
  );
  const alertMetrics = useMemo(() => getShopeePriceAlerts(mappings), [mappings]);
  const setWorkspaceFilters = workspace.setFilters;

  useEffect(() => {
    if (typeof setWorkspaceFilters !== "function") return undefined;
    const timer = setTimeout(() => {
      setWorkspaceFilters(current => mergeShopeeCatalogFilters(current, { search: searchTerm }));
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm, setWorkspaceFilters]);

  const filteredData = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return mappings.filter(item => {
      const matchChannel = selectedChannel === "all" || selectedChannel === "shopee";
      const matchSearch =
        !normalizedSearch ||
        (item.name || "").toLowerCase().includes(normalizedSearch) ||
        (item.sku || "").toLowerCase().includes(normalizedSearch) ||
        (item.barcode || "").includes(searchTerm);
      const matchStatus = statusFilter === "all" || item.status === statusFilter;
      return matchChannel && matchSearch && matchStatus;
    });
  }, [mappings, searchTerm, selectedChannel, statusFilter]);

  const loadedPricePercentages = {
    synced: percentOf(metrics.synced, metrics.loadedRows),
    variance: percentOf(metrics.variance, metrics.loadedRows),
    unavailable: percentOf(metrics.unavailable + metrics.errors, metrics.loadedRows)
  };
  const kpis = [
    { id: "total", icon: "simple-icon-doc", colorClass: "is-blue", label: intl.formatMessage({ id: "shopee.workspace.pricing.kpi.total" }), value: formatNumber(metrics.total), subtext: intl.formatMessage({ id: "shopee.workspace.inventory.sub.by-filter" }) },
    { id: "synced", icon: "simple-icon-check", colorClass: "is-green", label: intl.formatMessage({ id: "shopee.workspace.pricing.kpi.synced" }), value: formatNumber(metrics.synced), subtext: intl.formatMessage({ id: "shopee.workspace.inventory.kpi.synced-sub" }, { count: formatNumber(metrics.loadedRows) }) },
    { id: "diff", icon: "simple-icon-clock", colorClass: "is-orange", label: intl.formatMessage({ id: "shopee.workspace.pricing.kpi.variance" }), value: formatNumber(metrics.variance), subtext: intl.formatMessage({ id: "shopee.workspace.inventory.sub.on-page" }) },
    { id: "pending", icon: "simple-icon-hourglass", colorClass: "is-purple", label: intl.formatMessage({ id: "shopee.workspace.pricing.kpi.pending" }), value: formatNumber(metrics.unavailable), subtext: intl.formatMessage({ id: "shopee.workspace.pricing.kpi.pending-sub" }) },
    { id: "error", icon: "simple-icon-bell", colorClass: "is-red", label: intl.formatMessage({ id: "shopee.workspace.pricing.kpi.errors" }), value: formatNumber(metrics.errors), subtext: intl.formatMessage({ id: "shopee.workspace.inventory.sub.on-page" }) },
    {
      id: "last_sync",
      icon: "simple-icon-refresh",
      colorClass: "is-cyan",
      label: intl.formatMessage({ id: "shopee.workspace.inventory.kpi.last-sync" }),
      value: formatTimestamp(metrics.lastUpdatedAt) || intl.formatMessage({ id: "shopee.workspace.common.no-data" }),
      subtext: intl.formatMessage({ id: "shopee.workspace.inventory.sub.from-loaded-mappings" })
    }
  ];
  const channelTabs = [
    { key: "all", labelId: "shopee.workspace.catalog.channel-all", count: formatNumber(metrics.total) },
    { key: "shopee", labelId: null, label: "Shopee", count: formatNumber(metrics.total), markClass: "is-shopee", markText: "S" },
    { key: "tiktok", labelId: null, label: "TikTok Shop", count: "—", markClass: "is-tiktok", markText: "🎵" },
    { key: "lazada", labelId: null, label: "Lazada", count: "—", markClass: "is-lazada", markText: "L" },
    { key: "tiki", labelId: null, label: "Tiki", count: "—", markClass: "is-tiki", markText: "T" }
  ];
  const priceAlerts = [
    {
      id: "higher",
      type: "alert-red",
      icon: "simple-icon-bell",
      titleId: "shopee.workspace.pricing.alert.higher",
      count: alertMetrics.higher,
      descId: "shopee.workspace.pricing.alert.threshold-desc"
    },
    {
      id: "lower",
      type: "alert-orange",
      icon: "simple-icon-exclamation",
      titleId: "shopee.workspace.pricing.alert.lower",
      count: alertMetrics.lower,
      descId: "shopee.workspace.pricing.alert.threshold-desc"
    },
    {
      id: "unavailable",
      type: "alert-orange",
      icon: "simple-icon-clock",
      titleId: "shopee.workspace.pricing.alert.unavailable",
      count: alertMetrics.unavailable,
      descId: "shopee.workspace.inventory.sub.from-loaded-mappings"
    }
  ];
  const filters = workspace.filters || {};
  const pageSize = Math.max(1, numberValue((workspace.meta && workspace.meta.page_size) || filters.page_size) || 20);
  const currentPage = Math.max(1, numberValue((workspace.meta && workspace.meta.page) || filters.page) || 1);
  const totalPages = Math.max(1, numberValue((workspace.meta && workspace.meta.total_pages)) || Math.ceil(metrics.total / pageSize) || 1);
  const pageStart = filteredData.length ? (currentPage - 1) * pageSize + 1 : 0;
  const pageEnd = filteredData.length ? Math.min(pageStart + filteredData.length - 1, metrics.total) : 0;
  const visiblePages = useMemo(() => {
    const width = Math.min(5, totalPages);
    const first = Math.max(1, Math.min(currentPage - 2, totalPages - width + 1));
    return Array.from({ length: width }, (_, index) => first + index);
  }, [currentPage, totalPages]);
  const setPage = page => {
    if (typeof setWorkspaceFilters === "function" && page >= 1 && page <= totalPages) {
      setWorkspaceFilters(current => ({ ...current, page }));
    }
  };
  const setPageSize = event => {
    if (typeof setWorkspaceFilters === "function") {
      setWorkspaceFilters(current => ({ ...current, page: 1, page_size: Number(event.target.value) }));
    }
  };

  // Select row handlers
  const isAllSelected = selectedRows.length === filteredData.length && filteredData.length > 0;
  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData.map((d) => d.id));
    }
  }, [filteredData, isAllSelected]);

  const toggleSelectRow = useCallback((id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  // Render % difference label helper
  const renderDiffPct = (pctVal) => {
    const normalized = numberValue(pctVal);
    if (normalized === null) return <div className="text-muted" style={{ fontSize: "10px" }}>—</div>;
    if (normalized === 0) return <div className="text-muted" style={{ fontSize: "10px" }}>0%</div>;
    const isPos = normalized > 0;
    return (
      <div
        style={{
          fontSize: "10px",
          fontWeight: 600,
          color: isPos ? "#ef4444" : "#22c55e"
        }}
      >
        {isPos ? `+${formatPercent(normalized)}` : formatPercent(normalized)}
      </div>
    );
  };

  // Helper formula to compute channel price in modal
  const calculatePrice = (basePrice) => {
    let price = numberValue(basePrice);
    const markup = numberValue(markupPercent) || 0;
    const amount = numberValue(markupAmount) || 0;
    const packaging = numberValue(packagingCost) || 0;
    const fee = numberValue(channelFeePercent);
    if (price === null || fee === null || fee >= 100) return null;
    if (policyMode === "PERCENT_MARKUP") {
      price = price * (1 + markup / 100);
    } else if (policyMode === "FIXED_MARKUP") {
      price = price + amount;
    } else if (policyMode === "COST_PLUS") {
      price = (price + packaging) * (1 + markup / 100);
    }
    price = price / (1 - fee / 100);

    if (roundingRule === "THOUSAND") {
      price = Math.ceil(price / 1000) * 1000;
    } else if (roundingRule === "NINE_THOUSAND") {
      price = Math.floor(price / 10000) * 10000 + 9000;
    }
    return Math.round(price);
  };
  const policyPreviewBasePrice = mappings.find(row => row.vmass_price !== null)?.vmass_price ?? null;

  // ReactTable column definitions
  const columns = useMemo(
    () => [
      {
        Header: (
          <Input
            type="checkbox"
            checked={isAllSelected}
            onChange={toggleSelectAll}
            style={{ margin: 0, cursor: "pointer" }}
          />
        ),
        id: "select",
        accessor: "",
        sortable: false,
        width: 38,
        Cell: ({ original }) => (
          <Input
            type="checkbox"
            checked={selectedRows.includes(original.id)}
            onChange={() => toggleSelectRow(original.id)}
            style={{ margin: 0, cursor: "pointer" }}
          />
        )
      },
      {
        Header: (
          <div>
            <IntlMessages id="shopee.workspace.catalog.col-product" />
            <small><IntlMessages id="shopee.workspace.catalog.col-sku-vmass" /></small>
          </div>
        ),
        accessor: "name",
        minWidth: 170,
        Cell: ({ original }) => (
          <div className="shopee-inventory__product-cell">
            <div className="img-wrap">
              {original.image ? <img src={original.image} alt={original.name || ""} /> : <DummyImage />}
            </div>
            <div className="info">
              <span className="name" title={original.name || undefined}>
                {original.name || (
                  <IntlMessages id="shopee.workspace.inventory.no-name" />
                )}
              </span>
              <span className="code">{original.sku || "—"}</span>
            </div>
          </div>
        )
      },
      {
        Header: <IntlMessages id="shopee.workspace.pricing.col.vmass-price" />,
        accessor: "vmass_price",
        width: 120,
        Cell: ({ value }) => (
          <span style={{ fontWeight: 700, color: "#1e293b" }}>
            {money(value)}
          </span>
        )
      },
      {
        Header: (
          <div>
            <span className="channel-mark is-shopee">S</span> Shopee
            <small><IntlMessages id="shopee.workspace.pricing.col.price-small" /></small>
          </div>
        ),
        accessor: "shopee_price",
        width: 110,
        Cell: ({ original }) => (
          <div>
            <div style={{ fontWeight: 600, fontSize: "12px", color: "#1e293b" }}>
              {money(original.shopee_price)}
            </div>
            {renderDiffPct(original.shopee_diff_pct)}
          </div>
        )
      },
      {
        Header: (
          <div>
            <span className="channel-mark is-tiktok">🎵</span> TikTok Shop
            <small><IntlMessages id="shopee.workspace.pricing.col.price-small" /></small>
          </div>
        ),
        accessor: "tiktok_price",
        width: 115,
        Cell: () => (
          <div>
            <div style={{ fontWeight: 600, fontSize: "12px", color: "#1e293b" }}>
              —
            </div>
            <div className="text-muted" style={{ fontSize: "10px" }}>—</div>
          </div>
        )
      },
      {
        Header: (
          <div>
            <span className="channel-mark is-lazada">L</span> Lazada
            <small><IntlMessages id="shopee.workspace.pricing.col.price-small" /></small>
          </div>
        ),
        accessor: "lazada_price",
        width: 110,
        Cell: () => (
          <div>
            <div style={{ fontWeight: 600, fontSize: "12px", color: "#1e293b" }}>
              —
            </div>
            <div className="text-muted" style={{ fontSize: "10px" }}>—</div>
          </div>
        )
      },
      {
        Header: (
          <div>
            <span className="channel-mark is-tiki">T</span> Tiki
            <small><IntlMessages id="shopee.workspace.pricing.col.price-small" /></small>
          </div>
        ),
        accessor: "tiki_price",
        width: 105,
        Cell: () => (
          <div>
            <div style={{ fontWeight: 600, fontSize: "12px", color: "#1e293b" }}>
              —
            </div>
            <div className="text-muted" style={{ fontSize: "10px" }}>—</div>
          </div>
        )
      },
      {
        Header: (
          <div>
            <IntlMessages id="shopee.workspace.pricing.kpi.variance" />
            <small>(<IntlMessages id="shopee.workspace.pricing.col.diff-range" />)</small>
          </div>
        ),
        accessor: "diff_amount",
        width: 140,
        Cell: ({ original }) => (
          <div>
            <div style={{ fontWeight: 700, fontSize: "12px", color: "#1e293b" }}>
              {money(original.diff_amount)}
            </div>
            <div style={{ fontSize: "10px", color: original.diff_amount > 0 ? "#ef4444" : "#64748b" }}>
              ({formatPercent(original.diff_pct)})
            </div>
          </div>
        )
      },
      {
        Header: <IntlMessages id="shopee.workspace.returns.col.status" />,
        accessor: "status",
        width: 100,
        Cell: ({ original }) => (
          <span className={`badge-status status-${original.status}`}>
            <IntlMessages id={original.status_label_id} />
          </span>
        )
      },
      {
        Header: <IntlMessages id="shopee.workspace.inventory.col.updated" />,
        accessor: "updated_at",
        width: 110,
        Cell: ({ value }) => (
          <div style={{ fontSize: "11px", color: "#64748b", display: "flex", alignItems: "center", gap: "4px" }}>
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#22c55e", display: "inline-block" }} />
            {formatTimestamp(value) || intl.formatMessage({ id: "shopee.workspace.common.no-data" })}
          </div>
        )
      },
      {
        Header: <IntlMessages id="shopee.workspace.actions" />,
        accessor: "actions",
        sortable: false,
        width: 70,
        Cell: () => (
          <UncontrolledDropdown tag="div">
            <DropdownToggle color="light" size="xs" className="px-2 py-1 border-0" style={{ background: "#f1f5f9", borderRadius: "6px" }}>
              <i className="simple-icon-options font-weight-bold" />
            </DropdownToggle>
            <DropdownMenu right>
              <DropdownItem onClick={() => setRuleModal(true)}>
                <i className="simple-icon-settings mr-2" />{" "}
                <IntlMessages id="shopee.workspace.pricing.action.adjust-price" />
              </DropdownItem>
              <DropdownItem onClick={() => setApprovalModal(true)}>
                <i className="simple-icon-refresh mr-2" />{" "}
                <IntlMessages id="shopee.workspace.pricing.action.sync-price-list" />
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem className="text-danger">
                <i className="simple-icon-close mr-2" />{" "}
                <IntlMessages id="shopee.workspace.pricing.action.cancel-rule" />
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        )
      }
    ],
    [isAllSelected, selectedRows, toggleSelectAll, toggleSelectRow]
  );

  return (
    <div className="shopee-inventory">
      {/* Top Header Title & Actions Area */}
      <div className="shopee-inventory__header">
        <div className="title-area">
          <h2><IntlMessages id="shopee.workspace.tab.pricing" /></h2>
          <p><IntlMessages id="shopee.workspace.pricing.subtitle" /></p>
        </div>
        <div className="actions-area">
          <Button color="outline-primary" size="sm" onClick={() => setApprovalModal(true)}>
            <i className="simple-icon-reload" />{" "}
            <IntlMessages id="shopee.workspace.pricing.action.sync-price" />
          </Button>
          <Button color="outline-secondary" size="sm" onClick={() => setApprovalModal(true)}>
            <i className="simple-icon-pencil" />{" "}
            <IntlMessages id="shopee.workspace.pricing.action.batch-update" />
          </Button>
          <Button color="outline-secondary" size="sm" onClick={() => setRuleModal(true)}>
            <i className="simple-icon-settings" />{" "}
            <IntlMessages id="shopee.workspace.pricing.action.price-rules" />
          </Button>
          <Button color="outline-secondary" size="sm">
            <i className="simple-icon-cloud-upload" />{" "}
            <IntlMessages id="shopee.workspace.returns.export-excel" />
          </Button>
        </div>
      </div>

      {/* 6 KPI Cards Grid */}
      <div className="shopee-inventory__kpis">
        {kpis.map((kpi) => (
          <div key={kpi.id} className="shopee-inventory__kpi">
            <div className={`shopee-inventory__kpi-icon ${kpi.colorClass}`}>
              <i className={kpi.icon} />
            </div>
            <div className="shopee-inventory__kpi-info">
              <span className="label">{kpi.label}</span>
              <span className="value">{kpi.value}</span>
              <span className="subtext">{kpi.subtext}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Body: Main Matrix Table Column + Right Analytics Column */}
      <div className="shopee-inventory__body">
        {/* Left Column: Table and Filters */}
        <div className="shopee-inventory__main-col">
          {/* Toolbar Search and Filters */}
          <div className="shopee-inventory__toolbar">
            <div className="search-box">
              <i className="simple-icon-magnifier" />
              <Input
                type="text"
                placeholder={intl.formatMessage({
                  id: "shopee.workspace.pricing.search-placeholder"
                })}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="select-box">
              <Input type="select" value={selectedChannel} onChange={(e) => setSelectedChannel(e.target.value)}>
                <option value="all">{intl.formatMessage({ id: "shopee.workspace.common.all-channels" })}</option>
                <option value="shopee">Shopee</option>
                <option value="tiktok">TikTok Shop</option>
                <option value="lazada">Lazada</option>
                <option value="tiki">Tiki</option>
              </Input>
            </div>
            <div className="select-box">
              <Input type="select" defaultValue="all">
                <option value="all">{intl.formatMessage({ id: "shopee.workspace.pricing.category.all" })}</option>
                <option value="fashion">{intl.formatMessage({ id: "shopee.workspace.pricing.category.fashion" })}</option>
                <option value="tech">{intl.formatMessage({ id: "shopee.workspace.pricing.category.electronics" })}</option>
                <option value="food">{intl.formatMessage({ id: "shopee.workspace.pricing.category.food" })}</option>
              </Input>
            </div>
            <div className="select-box">
              <Input type="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">{intl.formatMessage({ id: "shopee.workspace.common.all-statuses" })}</option>
                <option value="dongbo">{intl.formatMessage({ id: "shopee.workspace.inventory.status.synced" })}</option>
                <option value="chenhlech">{intl.formatMessage({ id: "shopee.workspace.inventory.status.variance" })}</option>
              </Input>
            </div>
            <div className="select-box">
              <Input type="select" defaultValue="all">
                <option value="all">{intl.formatMessage({ id: "shopee.workspace.returns.all-warehouses" })}</option>
                <option value="main">{intl.formatMessage({ id: "shopee.workspace.pricing.warehouse.main" })}</option>
                <option value="hanoi">{intl.formatMessage({ id: "shopee.workspace.pricing.warehouse.hanoi" })}</option>
                <option value="hcm">{intl.formatMessage({ id: "shopee.workspace.pricing.warehouse.hcm" })}</option>
              </Input>
            </div>
            <Button color="outline-secondary" className="btn-filter">
              <i className="simple-icon-options" />{" "}
              <IntlMessages id="shopee.workspace.common.filter" />
            </Button>
          </div>

          {/* Table Container Card */}
          <div className="shopee-inventory__card">
            {/* Channel Tabs */}
            <div className="shopee-inventory__channel-tabs">
              {channelTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`tab-btn ${selectedChannel === tab.key ? "active" : ""}`}
                  onClick={() => setSelectedChannel(tab.key)}
                >
                  {tab.markClass && <span className={`channel-mark ${tab.markClass}`}>{tab.markText}</span>}
                  <span>{tab.labelId ? <IntlMessages id={tab.labelId} /> : tab.label}</span>
                  <span className="badge-count">{tab.count}</span>
                </button>
              ))}
            </div>

            {/* ReactTable Matrix */}
            <div className="shopee-inventory__table-wrapper">
              <ReactTable
                data={filteredData}
                columns={columns}
                defaultPageSize={pageSize}
                showPagination={false}
                loading={Boolean(workspace.loading)}
                noDataText={workspace.loading
                  ? intl.formatMessage({ id: "shopee.workspace.inventory.loading" })
                  : intl.formatMessage({ id: "shopee.workspace.inventory.empty" })}
                minRows={1}
                className="-striped -highlight"
              />
            </div>

            {/* Pagination Footer */}
            <div className="shopee-inventory__pagination">
              <div className="info-text">
                <IntlMessages
                  id="shopee.workspace.pricing.pagination-summary"
                  values={{
                    from: formatNumber(pageStart),
                    to: formatNumber(pageEnd),
                    total: formatNumber(metrics.total)
                  }}
                />
              </div>
              <div className="controls">
                <button type="button" className="btn-page" onClick={() => setPage(currentPage - 1)} disabled={workspace.loading || currentPage <= 1} aria-label={intl.formatMessage({ id: "shopee.workspace.common.previous" })}>
                  &lt;
                </button>
                {visiblePages.map(page => (
                  <button key={page} type="button" className={`btn-page ${page === currentPage ? "active" : ""}`} onClick={() => setPage(page)}>
                    {page}
                  </button>
                ))}
                {totalPages > visiblePages[visiblePages.length - 1] ? <span style={{ fontSize: "11px", color: "#94a3b8" }}>...</span> : null}
                {totalPages > visiblePages[visiblePages.length - 1] ? <button type="button" className="btn-page" onClick={() => setPage(totalPages)}>{totalPages}</button> : null}
                <button type="button" className="btn-page" onClick={() => setPage(currentPage + 1)} disabled={workspace.loading || currentPage >= totalPages} aria-label={intl.formatMessage({ id: "shopee.workspace.common.next" })}>&gt;</button>
                <Input type="select" className="page-size-select" value={pageSize} onChange={setPageSize}>
                  {["10", "20", "50"].map(size => (
                    <option key={size} value={size}>
                      <IntlMessages
                        id="shopee.workspace.common.per-page"
                        values={{ size }}
                      />
                    </option>
                  ))}
                </Input>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Analytics Cards */}
        <div className="shopee-inventory__side-col">
          {/* Donut Chart Summary Widget */}
          <div className="side-card">
            <div className="card-header-row">
              <h5><IntlMessages id="shopee.workspace.pricing.side.variance-title" /></h5>
            </div>
            <div className="donut-widget">
              <div className="donut-container">
                <svg viewBox="0 0 36 36">
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#e2e8f0"
                    strokeWidth="3.5"
                  />
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth="3.5"
                    strokeDasharray={`${loadedPricePercentages.synced}, 100`}
                    strokeDashoffset="0"
                  />
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth="3.5"
                    strokeDasharray={`${loadedPricePercentages.variance}, 100`}
                    strokeDashoffset={`-${loadedPricePercentages.synced}`}
                  />
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="3.5"
                    strokeDasharray={`${loadedPricePercentages.unavailable}, 100`}
                    strokeDashoffset={`-${loadedPricePercentages.synced + loadedPricePercentages.variance}`}
                  />
                </svg>
                <div className="donut-center-text">
                  <span className="total">{formatNumber(metrics.variance)}</span>
                  <span className="sub"><IntlMessages id="shopee.workspace.pricing.sub.on-page-short" /></span>
                </div>
              </div>
              <div className="legend-list">
                <div className="legend-item">
                  <div className="label-wrap">
                    <span className="dot green" />
                    <span><IntlMessages id="shopee.workspace.inventory.status.synced" /></span>
                  </div>
                  <div className="val">
                    {formatNumber(metrics.synced)} <span className="percent">({formatPercent(loadedPricePercentages.synced)})</span>
                  </div>
                </div>
                <div className="legend-item">
                  <div className="label-wrap">
                    <span className="dot orange" />
                    <span><IntlMessages id="shopee.workspace.inventory.status.variance" /></span>
                  </div>
                  <div className="val">
                    {formatNumber(metrics.variance)} <span className="percent">({formatPercent(loadedPricePercentages.variance)})</span>
                  </div>
                </div>
                <div className="legend-item">
                  <div className="label-wrap">
                    <span className="dot red" />
                    <span><IntlMessages id="shopee.workspace.pricing.kpi.pending" /></span>
                  </div>
                  <div className="val">
                    {formatNumber(metrics.unavailable + metrics.errors)} <span className="percent">({formatPercent(loadedPricePercentages.unavailable)})</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Top Price Variance Items Widget */}
          <div className="side-card">
            <div className="card-header-row">
              <h5><IntlMessages id="shopee.workspace.pricing.side.top-variance" /></h5>
              <a href="#xem-tat-ca" onClick={(e) => e.preventDefault()}>
                <IntlMessages id="shopee.workspace.common.view-all" />
              </a>
            </div>
            <div className="top-variance-list">
              {topPriceVarianceProducts.length ? topPriceVarianceProducts.map((item, index) => (
                <div key={item.id || `${item.platform_item_id}-${item.platform_model_id}`} className="variance-item">
                  <div className="left-info">
                    <span className="rank">{index + 1}</span>
                    <div className="thumb d-flex align-items-center justify-content-center bg-light">
                      <i className="simple-icon-tag text-muted" style={{ fontSize: "10px" }} />
                    </div>
                    <div className="details">
                      <span className="name">
                        {item.name || (
                          <IntlMessages id="shopee.workspace.inventory.no-name" />
                        )}
                      </span>
                      <span className="sku">{item.sku || "—"}</span>
                    </div>
                  </div>
                  <div className="var-value">
                    {item.price_diff > 0 ? "+" : item.price_diff < 0 ? "-" : ""}{money(Math.abs(item.price_diff))} <span style={{ fontSize: "10px", fontWeight: 400, color: "#ef4444" }}>({formatPercent(item.shopee_diff_pct === null ? null : Math.abs(item.shopee_diff_pct))})</span>
                  </div>
                </div>
              )) : (
                <div className="p-3 text-muted small">
                  <IntlMessages id="shopee.workspace.pricing.side.no-variance" />
                </div>
              )}
            </div>
          </div>

          {/* Price Alerts Widget */}
          <div className="side-card">
            <div className="card-header-row">
              <h5><IntlMessages id="shopee.workspace.pricing.side.alerts-title" /></h5>
              <a href="#xem-tat-ca" onClick={(e) => e.preventDefault()}>
                <IntlMessages id="shopee.workspace.common.view-all" />
              </a>
            </div>
            <div className="alert-list">
              {priceAlerts.map((alert) => (
                <div key={alert.id} className={`alert-item ${alert.type}`}>
                  <div className="icon">
                    <i className={alert.icon} />
                  </div>
                  <div className="text-wrap">
                    <span className="alert-title">
                      <IntlMessages
                        id={alert.titleId}
                        values={{ count: formatNumber(alert.count) }}
                      />
                    </span>
                    <span className="alert-desc"><IntlMessages id={alert.descId} /></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modal 1: Price Policy Builder Modal */}
      <Modal isOpen={ruleModal} toggle={() => setRuleModal(false)} size="lg">
        <ModalHeader toggle={() => setRuleModal(false)}>
          <IntlMessages id="shopee.workspace.pricing.rule.title" />
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="6" className="mb-3">
              <FormGroup>
                <Label className="font-weight-bold"><IntlMessages id="shopee.workspace.pricing.rule.policy-mode" /></Label>
                <Input
                  type="select"
                  value={policyMode}
                  onChange={(e) => setPolicyMode(e.target.value)}
                >
                  <option value="DIRECT">{intl.formatMessage({ id: "shopee.workspace.pricing.rule.option-direct" })}</option>
                  <option value="PERCENT_MARKUP">{intl.formatMessage({ id: "shopee.workspace.pricing.rule.option-percent" })}</option>
                  <option value="FIXED_MARKUP">{intl.formatMessage({ id: "shopee.workspace.pricing.rule.option-fixed" })}</option>
                  <option value="COST_PLUS">{intl.formatMessage({ id: "shopee.workspace.pricing.rule.option-cost-plus" })}</option>
                </Input>
              </FormGroup>
            </Col>

            {policyMode === "PERCENT_MARKUP" || policyMode === "COST_PLUS" ? (
              <Col md="6" className="mb-3">
                <FormGroup>
                  <Label><IntlMessages id="shopee.workspace.pricing.rule.markup-percent-label" /></Label>
                  <Input
                    type="number"
                    value={markupPercent}
                    onChange={(e) => setMarkupPercent(e.target.value)}
                  />
                </FormGroup>
              </Col>
            ) : null}

            {policyMode === "FIXED_MARKUP" ? (
              <Col md="6" className="mb-3">
                <FormGroup>
                  <Label><IntlMessages id="shopee.workspace.pricing.rule.markup-amount-label" /></Label>
                  <Input
                    type="number"
                    value={markupAmount}
                    onChange={(e) => setMarkupAmount(e.target.value)}
                  />
                </FormGroup>
              </Col>
            ) : null}

            <Col md="4" className="mb-3">
              <FormGroup>
                <Label><IntlMessages id="shopee.workspace.pricing.rule.fee-label" /></Label>
                  <Input
                    type="number"
                    value={channelFeePercent}
                    onChange={(e) => setChannelFeePercent(e.target.value)}
                />
              </FormGroup>
            </Col>

            <Col md="4" className="mb-3">
              <FormGroup>
                <Label><IntlMessages id="shopee.workspace.pricing.rule.packaging-label" /></Label>
                  <Input
                    type="number"
                    value={packagingCost}
                    onChange={(e) => setPackagingCost(e.target.value)}
                />
              </FormGroup>
            </Col>

            <Col md="4" className="mb-3">
              <FormGroup>
                <Label><IntlMessages id="shopee.workspace.pricing.rule.rounding-label" /></Label>
                <Input
                  type="select"
                  value={roundingRule}
                  onChange={(e) => setRoundingRule(e.target.value)}
                >
                  <option value="NONE">{intl.formatMessage({ id: "shopee.workspace.pricing.rule.rounding-none" })}</option>
                  <option value="THOUSAND">{intl.formatMessage({ id: "shopee.workspace.pricing.rule.rounding-thousand" })}</option>
                  <option value="NINE_THOUSAND">{intl.formatMessage({ id: "shopee.workspace.pricing.rule.rounding-nine-thousand" })}</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <Alert color="info" className="mb-0 mt-2">
            <small>
              <strong><IntlMessages id="shopee.workspace.pricing.rule.formula-strong" /></strong>{" "}
              <IntlMessages id="shopee.workspace.pricing.rule.formula-text" />
              {policyPreviewBasePrice === null ? (
                <IntlMessages id="shopee.workspace.pricing.rule.preview-no-base" />
              ) : (
                ` ${intl.formatMessage(
                  { id: "shopee.workspace.pricing.rule.preview-from" },
                  {
                    base: money(policyPreviewBasePrice),
                    result: money(calculatePrice(policyPreviewBasePrice))
                  }
                )}`
              )}
            </small>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setRuleModal(false)}>
            <IntlMessages id="shopee.workspace.common.close" />
          </Button>
          <Button
            color="primary"
            onClick={() => {
              setRuleModal(false);
              setApprovalModal(true);
            }}
          >
            <IntlMessages id="shopee.workspace.pricing.rule.save-preview" />
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal 2: Batch Publish Confirmation Modal */}
      <Modal isOpen={approvalModal} toggle={() => setApprovalModal(false)} size="lg">
        <ModalHeader toggle={() => setApprovalModal(false)}>
          <IntlMessages id="shopee.workspace.pricing.approve.title" />
        </ModalHeader>
        <ModalBody>
          <Alert color="warning">
            <strong><IntlMessages id="shopee.workspace.pricing.approve.warning-strong" /></strong>{" "}
            <IntlMessages id="shopee.workspace.pricing.approve.warning-detail" />
          </Alert>
          <Row className="text-center my-3">
            <Col md="4">
              <div className="text-muted small"><IntlMessages id="shopee.workspace.pricing.approve.items-total" /></div>
              <div className="h4 font-weight-bold">{formatNumber(metrics.total)}</div>
            </Col>
            <Col md="4">
              <div className="text-muted small"><IntlMessages id="shopee.workspace.pricing.approve.rule-applied" /></div>
              <div className="h4 text-primary font-weight-bold">{policyMode}</div>
            </Col>
            <Col md="4">
              <div className="text-muted small"><IntlMessages id="shopee.workspace.pricing.approve.fee-deducted" /></div>
              <div className="h4 text-danger font-weight-bold">{channelFeePercent === "" ? "—" : `${channelFeePercent}%`}</div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setApprovalModal(false)}>
            <IntlMessages id="shopee.cancel" />
          </Button>
          <Button
            color="success"
            onClick={() => {
              if (workspace?.runAction) {
                workspace.runAction(
                  "publish-prices",
                  (key) => ShopeeV2Api.remediateIssues(workspace.selectedShopId, "publish_price_batch", [], key),
                  intl.formatMessage({ id: "shopee.workspace.pricing.approve.queued" })
                );
              }
              setApprovalModal(false);
            }}
          >
            <IntlMessages id="shopee.workspace.pricing.approve.confirm" />
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
})

